import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { MapPin, Route as RouteIcon, Search, Users, Receipt } from "lucide-react";
import { apiBaseUrl, authHeaders } from "./shared";
import LoadingState from "./LoadingState";
import Pagination from "./Pagination";
import RouteMonitorModal from "../../components/RouteMonitorModal";
import {
  fmtPct, fmtNum, varianceSentence, varianceColor,
  Kpi, Band, ReconcileBanner, MethodologyButton, MethodologyDrawer, TransactionsDrawer,
} from "./ComplianceUI";

const TOLL_METHODOLOGY = [
  { term: "Company Actual", text: "All real toll passes in the period, by crossing time. = Attributed + Unattributed." },
  { term: "Attributed", text: "Toll spend assigned to a specific load (load_id set)." },
  { term: "Unattributed", text: "Real toll spend not yet assignable to a load (deadhead / forgotten pickup / conflict, e.g. load_id NULL). Counted in Company Actual; excluded from load-level variance; not a violation." },
  { term: "Attribution Coverage", text: "Attributed ÷ Company Actual." },
  { term: "Comparable Loads", text: "Loads with BOTH attributed toll passes AND a chosen-route toll estimate — the only population where planned vs actual is like-for-like." },
  { term: "Planned Toll Cost", text: "Chosen-route toll estimate summed over the comparable loads." },
  { term: "Actual Toll Cost", text: "Actual toll passes summed over the SAME comparable loads." },
  { term: "Variance to Plan", text: "Actual − Planned on the comparable loads only. Descriptive ('additional toll cost vs plan'), not a claim it was avoidable." },
];

const PAGE_SIZE = 25;
const GROUPED_PAGE_SIZE = 200;

const SORT_OPTIONS = [
  { value: "variance", label: "Most over plan" },
  { value: "total", label: "Most total tolls" },
  { value: "passes", label: "Most passes" },
];

function useDebounced(value, delayMs) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}
function weekAgoIso() {
  const d = new Date();
  d.setDate(d.getDate() - 7);
  return d.toISOString().slice(0, 10);
}
const ALL_TIME_START = "2020-01-01";
function fmtMoney(n) {
  if (n == null) return "—";
  const sign = n < 0 ? "-" : "";
  return `${sign}$${Math.abs(n).toLocaleString("en-US", { maximumFractionDigits: 2 })}`;
}
function fmtSignedMoney(n) {
  if (n == null) return "—";
  const sign = n > 0 ? "+" : n < 0 ? "-" : "";
  return `${sign}$${Math.abs(n).toLocaleString("en-US", { maximumFractionDigits: 2 })}`;
}

// Grouped by TRUCK, not driver -- same reasoning as Fuel Compliance
// (no historical driver-per-load record in this schema). Two numbers per
// truck/load: what dispatch planned to pay in tolls (the HERE estimate for
// the route actually chosen at booking time) vs what was actually paid
// (every real toll pass, regardless of which route it happened to match --
// that classification still exists and still drives the map view, it's
// just not the headline anymore). Click "View Route" for the real,
// gantry-by-gantry breakdown on the map.
export default function TollCompliance() {
  const [start, setStart] = useState(weekAgoIso());
  const [end, setEnd] = useState(todayIso());
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState("variance");
  const [rawDriverSearch, setRawDriverSearch] = useState("");
  const driverSearch = useDebounced(rawDriverSearch, 300);
  const [groupByDispatcher, setGroupByDispatcher] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(() => new Set());
  const [openRoute, setOpenRoute] = useState(null); // { loadId, unitNumber } | null
  const [showMethod, setShowMethod] = useState(false);
  const [showTxns, setShowTxns] = useState(false);

  const effectivePageSize = groupByDispatcher ? GROUPED_PAGE_SIZE : PAGE_SIZE;

  useEffect(() => {
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [start, end, sortBy, driverSearch, groupByDispatcher]);

  useEffect(() => {
    setLoading(true);
    setError(null);
    const params = { start, end, page, page_size: effectivePageSize, sort_by: sortBy };
    if (driverSearch.trim()) params.driver_search = driverSearch.trim();
    axios
      .get(`${apiBaseUrl}/scripted/fleet/toll-compliance`, { ...authHeaders(), params })
      .then((res) => setData(res.data))
      .catch((err) => setError(err.response?.data?.detail || err.message))
      .finally(() => setLoading(false));
  }, [start, end, page, effectivePageSize, sortBy, driverSearch]);

  function toggle(truckId) {
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(truckId) ? next.delete(truckId) : next.add(truckId);
      return next;
    });
  }

  const trucks = data?.trucks || [];

  const groups = useMemo(() => {
    if (!groupByDispatcher) return null;
    const byDispatcher = new Map();
    for (const t of trucks) {
      const key = t.dispatcher_name || "Unassigned";
      if (!byDispatcher.has(key)) byDispatcher.set(key, []);
      byDispatcher.get(key).push(t);
    }
    return [...byDispatcher.entries()].sort(([a], [b]) => {
      if (a === "Unassigned") return 1;
      if (b === "Unassigned") return -1;
      return a.localeCompare(b);
    });
  }, [groupByDispatcher, trucks]);

  return (
    <div>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Toll Compliance</h1>
          <div style={styles.subtitle}>
            Planned vs actual tolls, by truck. Planned is the pre-trip estimate for the route dispatch chose;
            Actual is every real toll pass recorded, whichever road it happened on. Open a load's route to see the
            real gantry-by-gantry breakdown on the map.
          </div>
        </div>
        <div style={styles.dateRange}>
          <button
            type="button"
            style={{ ...styles.presetBtn, ...(start === weekAgoIso() ? styles.presetBtnActive : {}) }}
            onClick={() => { setStart(weekAgoIso()); setEnd(todayIso()); }}
          >
            This week
          </button>
          <button
            type="button"
            style={{ ...styles.presetBtn, ...(start === ALL_TIME_START ? styles.presetBtnActive : {}) }}
            onClick={() => { setStart(ALL_TIME_START); setEnd(todayIso()); }}
          >
            All time
          </button>
          <input type="date" value={start} onChange={(e) => setStart(e.target.value)} style={styles.dateInput} max={end} />
          <span style={styles.dateSep}>to</span>
          <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} style={styles.dateInput} min={start} max={todayIso()} />
        </div>
      </div>

      {error && <div style={styles.errorBox}>Couldn't load toll compliance: {error}</div>}
      {loading && <LoadingState label="Loading toll compliance…" />}

      {!loading && !error && (
        <>
          <TollV2Overview data={data} driverActive={!!driverSearch.trim()} onMethod={() => setShowMethod(true)} onTxns={() => setShowTxns(true)} />

          <div style={styles.toolbar}>
            <div style={styles.searchWrap}>
              <Search size={14} style={styles.searchIcon} />
              <input
                style={styles.searchInput}
                type="search"
                placeholder="Search by driver name..."
                value={rawDriverSearch}
                onChange={(e) => setRawDriverSearch(e.target.value)}
              />
            </div>
            <select style={styles.select} value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              {SORT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <button
              type="button"
              style={{ ...styles.groupBtn, ...(groupByDispatcher ? styles.groupBtnActive : {}) }}
              onClick={() => setGroupByDispatcher((v) => !v)}
            >
              <Users size={13} /> Group by dispatcher
            </button>
          </div>

          {trucks.length === 0 && (
            <div style={styles.emptyState}>
              <RouteIcon size={22} color="var(--muted)" />
              <div style={styles.emptyTitle}>
                {rawDriverSearch.trim() ? `No trucks match "${rawDriverSearch.trim()}"` : "No toll passes recorded in this range"}
              </div>
              <div style={styles.emptySubtitle}>
                {rawDriverSearch.trim() ? "Try a different driver name or clear the search." : "A truck needs a real, priced toll pass on a load to show up here."}
              </div>
            </div>
          )}

          {groupByDispatcher && groups
            ? groups.map(([dispatcherName, groupTrucks]) => (
                <div key={dispatcherName} style={styles.dispatcherGroup}>
                  <div style={styles.dispatcherGroupHead}>
                    <span style={styles.dispatcherGroupName}>{dispatcherName}</span>
                    <span style={styles.dispatcherGroupCount}>{groupTrucks.length} truck{groupTrucks.length === 1 ? "" : "s"}</span>
                  </div>
                  <div style={styles.truckList}>
                    {groupTrucks.map((t) => (
                      <TruckCard key={t.truck_id} t={t} isOpen={expanded.has(t.truck_id)} onToggle={() => toggle(t.truck_id)} onViewRoute={setOpenRoute} />
                    ))}
                  </div>
                </div>
              ))
            : trucks.length > 0 && (
                <div style={styles.truckList}>
                  {trucks.map((t) => (
                    <TruckCard key={t.truck_id} t={t} isOpen={expanded.has(t.truck_id)} onToggle={() => toggle(t.truck_id)} onViewRoute={setOpenRoute} />
                  ))}
                </div>
              )}

          {!groupByDispatcher && trucks.length > 0 && (
            <Pagination page={data.page} totalPages={data.total_pages} totalCount={data.total_count} pageSize={data.page_size} onPageChange={setPage} />
          )}
        </>
      )}

      {openRoute && (
        <RouteMonitorModal loadId={openRoute.loadId} unitNumber={openRoute.unitNumber} onClose={() => setOpenRoute(null)} />
      )}
      <MethodologyDrawer open={showMethod} onClose={() => setShowMethod(false)} title="Toll Compliance — definitions" items={TOLL_METHODOLOGY} />
      {showTxns && <TransactionsDrawer kind="toll" start={start} end={end} onClose={() => setShowTxns(false)} />}
    </div>
  );
}

// v2 overview: COST -> COVERAGE -> COMPARABLE IMPACT, strictly like-for-like
// (planned & actual over the SAME comparable-load population).
function TollV2Overview({ data, driverActive, onMethod, onTxns }) {
  const cost = data?.cost || {};
  const comp = data?.compliance || {};
  // driver_search scopes only part of the backend blocks (cost is driver-filtered,
  // the like-for-like comparison is not), so the bands would be a mixed population.
  if (driverActive) {
    return (
      <div style={styles.scopeNote}>
        Cost / Coverage / Comparable Impact are <b>fleet-wide</b> for the selected dates — they are not
        driver-scoped (no reliable historical driver→spend mapping). Clear the driver filter to view them.
        The truck list below is filtered to matching trucks.
      </div>
    );
  }
  return (
    <>
      <div style={styles.overviewTop}>
        <MethodologyButton onClick={onMethod} />
        <button type="button" style={styles.txnBtn} onClick={onTxns}><Receipt size={13} /> View toll transactions</button>
      </div>

      <ReconcileBanner companyActual={cost.company_actual} attributed={cost.attributed} unattributed={cost.unattributed} />

      <Band title="Cost — company financial truth">
        <Kpi tone="primary" label="Company Actual" value={fmtMoney(cost.company_actual)} sub="all real toll passes"
             tip="All real toll passes in the period, by crossing time. = Attributed + Unattributed." />
        <Kpi label="Attributed" value={fmtMoney(cost.attributed)} sub="assigned to a load" />
        <Kpi tone="amber" label="Unattributed" valueColor="#d97706" value={fmtMoney(cost.unattributed)} sub="real spend, no load yet"
             tip="Real toll spend not yet assignable to a load (e.g. load_id NULL). Counted in Company Actual; excluded from load-level variance; NOT a violation." />
      </Band>

      <Band title="Coverage — how much we can trust / evaluate">
        <Kpi label="Attribution Coverage" value={fmtPct(cost.attribution_coverage_pct)}
             sub={`${fmtMoney(cost.attributed)} of ${fmtMoney(cost.company_actual)}`}
             tip="Attributed ÷ Company Actual." />
        <Kpi label="Comparable Loads" value={fmtNum(comp.comparable_loads)} sub="planned AND actual present"
             tip="Loads with both attributed toll passes and a chosen-route toll estimate — the only like-for-like population." />
      </Band>

      <Band
        title="Comparable Impact — performance against plan"
        note={comp.comparable_loads != null ? `Based on ${fmtNum(comp.comparable_loads)} comparable loads` : "No comparable population"}
      >
        <Kpi label="Planned Toll Cost" value={fmtMoney(comp.planned_comparable)} sub="chosen-route estimate" />
        <Kpi label="Actual Toll Cost" value={fmtMoney(comp.actual_comparable)} sub="same loads, actual" />
        <Kpi label="Variance to Plan" value={varianceSentence(comp.variance_to_plan)} valueColor={varianceColor(comp.variance_to_plan)}
             sub="additional toll cost vs plan"
             tip="Actual − Planned on comparable loads only. Descriptive, not a claim it was avoidable." />
      </Band>
    </>
  );
}

function TruckCard({ t, isOpen, onToggle, onViewRoute }) {
  const over = t.variance > 0;
  return (
    <div style={styles.truckCard}>
      <div style={styles.truckHead} onClick={onToggle}>
        <div style={styles.truckId}>
          <div style={styles.truckUnit}>Truck #{t.unit_number}</div>
          <div style={styles.truckDriver}>{t.driver_name || "Unassigned"}{t.dispatcher_name ? ` · ${t.dispatcher_name}` : ""}</div>
        </div>
        <div style={styles.planVActual}>
          <div style={styles.planVActualCol}>
            <div style={styles.planVActualLabel}>Planned</div>
            <div style={styles.planVActualValue}>{fmtMoney(t.estimated_total)}</div>
          </div>
          <div style={styles.planVActualCol}>
            <div style={styles.planVActualLabel}>Actual</div>
            <div style={styles.planVActualValue}>{fmtMoney(t.total)}</div>
          </div>
          <div style={{ ...styles.chip, ...(over ? styles.chipWarn : styles.chipGood) }}>
            {fmtSignedMoney(t.variance)}
          </div>
        </div>
        <div style={styles.truckStat}>{t.pass_count} pass{t.pass_count === 1 ? "" : "es"}</div>
        <span style={{ marginLeft: "auto", color: "var(--muted)" }}>{isOpen ? "▲" : "▼"}</span>
      </div>
      {isOpen && (
        <div style={styles.loadsWrap}>
          {t.loads.length === 0 && <div style={styles.stopEmpty}>No loads with toll data in this range.</div>}
          {t.loads.map((ld) => (
            <LoadDetail key={ld.load_id} ld={ld} unitNumber={t.unit_number} onViewRoute={onViewRoute} />
          ))}
        </div>
      )}
    </div>
  );
}

function LoadDetail({ ld, unitNumber, onViewRoute }) {
  const over = ld.variance > 0;
  return (
    <div style={styles.loadBlock}>
      <div style={styles.loadHead}>
        <div style={styles.loadHeadLeft}>
          <span style={styles.loadLane}>{ld.lane}</span>
          <span style={styles.loadStatus}>{ld.status}</span>
        </div>
        <button type="button" style={styles.viewRouteBtn} title="See this load's real route on the map" onClick={() => onViewRoute({ loadId: ld.load_id, unitNumber })}>
          <MapPin size={12} /> View Route
        </button>
      </div>
      <div style={styles.loadNumbers}>
        <div style={styles.loadNumber}>
          <span style={styles.loadNumberLabel}>Planned</span>
          <span style={styles.loadNumberValue}>{fmtMoney(ld.estimated)}</span>
        </div>
        <div style={styles.loadNumber}>
          <span style={styles.loadNumberLabel}>Actual</span>
          <span style={styles.loadNumberValue}>{fmtMoney(ld.total)}</span>
        </div>
        <span style={{ ...styles.chip, ...(over ? styles.chipWarn : styles.chipGood) }}>{fmtSignedMoney(ld.variance)}</span>
      </div>
    </div>
  );
}

const styles = {
  header: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 20, gap: 20, flexWrap: "wrap" },
  title: { fontSize: 22, fontWeight: 800, color: "var(--ink)", margin: 0 },
  subtitle: { fontSize: 13, color: "var(--muted)", marginTop: 4, maxWidth: 680 },
  dateRange: { display: "flex", alignItems: "center", gap: 8, flexShrink: 0, flexWrap: "wrap" },
  dateInput: { fontSize: 12.5, color: "var(--ink-2)", background: "var(--card)", border: "1px solid var(--line)", borderRadius: 8, padding: "7px 10px" },
  dateSep: { fontSize: 12, color: "var(--muted)" },
  presetBtn: {
    fontSize: 11.5, fontWeight: 600, color: "var(--ink-2)", background: "var(--card)",
    border: "1px solid var(--line)", borderRadius: 8, padding: "7px 10px", cursor: "pointer", whiteSpace: "nowrap",
  },
  presetBtnActive: { color: "var(--brand)", borderColor: "var(--brand)", background: "rgba(37,99,235,0.06)" },

  errorBox: {
    background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.2)", color: "#dc2626",
    borderRadius: "var(--radius-md)", padding: "12px 16px", fontSize: 13, marginBottom: 16,
  },

  summaryRow: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 14, marginBottom: 20 },
  summaryCard: { background: "var(--card)", border: "1px solid var(--line)", borderRadius: "var(--radius-lg)", padding: "16px 18px", boxShadow: "var(--shadow-xs)" },
  summaryCardWarn: { borderColor: "rgba(217,119,6,0.3)", background: "linear-gradient(155deg, rgba(217,119,6,0.07), var(--card) 62%)" },
  summaryValue: { fontSize: 20, fontWeight: 800, color: "var(--ink)", lineHeight: 1.2 },
  summaryLabel: { fontSize: 12, color: "var(--muted)", marginTop: 2 },
  summarySubline: { fontSize: 11, color: "var(--muted)", marginTop: 4, fontVariantNumeric: "tabular-nums" },

  scopeNote: { background: "var(--card-2)", border: "1px dashed var(--line)", borderRadius: "var(--radius-md)", padding: "12px 16px", fontSize: 12.5, color: "var(--muted)", marginBottom: 16, lineHeight: 1.5 },
  overviewTop: { display: "flex", alignItems: "center", gap: 8, justifyContent: "flex-end", marginBottom: 12, flexWrap: "wrap" },
  txnBtn: {
    display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 600,
    color: "var(--brand)", background: "rgba(37,99,235,0.06)", border: "1px solid rgba(37,99,235,0.25)",
    borderRadius: 8, padding: "7px 12px", cursor: "pointer", whiteSpace: "nowrap",
  },

  toolbar: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 12 },
  searchWrap: { position: "relative", flex: "1 1 220px", minWidth: 180 },
  searchIcon: { position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--muted)" },
  searchInput: {
    width: "100%", boxSizing: "border-box", fontSize: 12.5, padding: "8px 10px 8px 30px",
    borderRadius: 8, border: "1px solid var(--line)", background: "var(--card)", color: "var(--ink)",
  },
  select: {
    fontSize: 12, padding: "7px 10px", borderRadius: 8, border: "1px solid var(--line)",
    background: "var(--card)", color: "var(--ink)", maxWidth: 200, flexShrink: 0,
  },
  groupBtn: {
    display: "flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 600,
    color: "var(--ink-2)", background: "var(--card)", border: "1px solid var(--line)",
    borderRadius: 8, padding: "7px 12px", cursor: "pointer", flexShrink: 0, whiteSpace: "nowrap",
  },
  groupBtnActive: { color: "var(--brand)", borderColor: "var(--brand)", background: "rgba(37,99,235,0.06)" },

  dispatcherGroup: { marginBottom: 16 },
  dispatcherGroupHead: { display: "flex", alignItems: "baseline", gap: 8, padding: "4px 2px 8px" },
  dispatcherGroupName: { fontSize: 13, fontWeight: 700, color: "var(--ink)" },
  dispatcherGroupCount: { fontSize: 11.5, color: "var(--muted)" },

  emptyState: {
    display: "flex", flexDirection: "column", alignItems: "center", gap: 6, textAlign: "center",
    background: "var(--card)", border: "1px solid var(--line)", borderRadius: "var(--radius-lg)", padding: "40px 20px", marginBottom: 12,
  },
  emptyTitle: { fontSize: 14, fontWeight: 700, color: "var(--ink)" },
  emptySubtitle: { fontSize: 12.5, color: "var(--muted)" },

  truckList: { display: "flex", flexDirection: "column", gap: 8 },
  truckCard: { background: "var(--card)", border: "1px solid var(--line)", borderRadius: "var(--radius-md)", overflow: "hidden" },
  truckHead: { display: "flex", alignItems: "center", gap: 18, padding: "12px 16px", cursor: "pointer" },
  truckId: { minWidth: 168 },
  truckUnit: { fontSize: 13, fontWeight: 700, color: "var(--ink)" },
  truckDriver: { fontSize: 11, color: "var(--muted)", marginTop: 1 },

  planVActual: { display: "flex", alignItems: "center", gap: 18 },
  planVActualCol: { minWidth: 70, textAlign: "right" },
  planVActualLabel: { fontSize: 10, fontWeight: 600, color: "var(--muted)", textTransform: "uppercase", letterSpacing: 0.3 },
  planVActualValue: { fontSize: 13.5, fontWeight: 700, color: "var(--ink)", fontVariantNumeric: "tabular-nums" },

  truckStat: { fontSize: 11.5, color: "var(--muted)", whiteSpace: "nowrap" },

  chip: { fontSize: 11, fontWeight: 800, padding: "3px 9px", borderRadius: 999, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" },
  chipGood: { background: "rgba(22,163,74,0.1)", color: "#16a34a" },
  chipWarn: { background: "rgba(217,119,6,0.1)", color: "#d97706" },

  loadsWrap: { borderTop: "1px solid var(--line)", display: "flex", flexDirection: "column" },
  loadBlock: { padding: "12px 16px", borderBottom: "1px solid var(--line)", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 14, flexWrap: "wrap" },
  loadHead: { display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" },
  loadHeadLeft: { display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" },
  loadLane: { fontSize: 12.5, fontWeight: 700, color: "var(--ink)" },
  loadStatus: { fontSize: 11, color: "var(--muted)", textTransform: "capitalize" },
  viewRouteBtn: {
    display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, fontWeight: 600,
    color: "var(--brand)", background: "rgba(37,99,235,0.06)", border: "1px solid rgba(37,99,235,0.2)",
    borderRadius: 6, padding: "3px 8px", cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0,
  },

  loadNumbers: { display: "flex", alignItems: "center", gap: 16, flexShrink: 0 },
  loadNumber: { display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 1, minWidth: 60 },
  loadNumberLabel: { fontSize: 9.5, fontWeight: 600, color: "var(--muted)", textTransform: "uppercase", letterSpacing: 0.3 },
  loadNumberValue: { fontSize: 12.5, fontWeight: 700, color: "var(--ink)", fontVariantNumeric: "tabular-nums" },

  stopEmpty: { fontSize: 12, color: "var(--muted)", padding: "8px 2px" },
};
